import type { ControllerConfig, EstimatedParams, ExperimentConfig, PlantParams, Schedule } from './types';

/** Equilibrium thrust T_0 = m g + k (z_0 − z_s). */
export function equilibriumThrust(p: { m: number; g: number; k: number; zs: number }, z0: number): number {
  return p.m * p.g + p.k * (z0 - p.zs);
}

/** Net upward force: T − m g − b v − k (z − z_s) + d. */
export function netForce(p: PlantParams, z: number, v: number, T: number, d: number): number {
  return T - p.m * p.g - p.b * v - p.k * (z - p.zs) + d;
}

/** Individual force components, for the scene arrows and force-balance lessons. */
export function forceComponents(p: PlantParams, z: number, v: number, T: number, d: number) {
  return {
    thrust: T,
    gravity: -p.m * p.g,
    damping: -p.b * v,
    spring: -p.k * (z - p.zs),
    disturbance: d,
    net: netForce(p, z, v, T, d),
  };
}

export function scheduleValue(s: Schedule, t: number): number {
  let value = s.initial;
  for (const step of s.steps) {
    // A tiny tolerance keeps step-aligned events on the intended sample despite float time.
    if (t + 1e-9 >= step.t) value = step.value;
    else break;
  }
  return value;
}

export function estimatedParams(plant: PlantParams, c: ControllerConfig): EstimatedParams {
  return c.estimated ?? { m: plant.m, g: plant.g, k: plant.k, zs: plant.zs };
}

/** State-space matrices for the linearized deviation model (see plan.md). */
export function stateSpace(p: PlantParams) {
  return {
    A: [
      [0, 1],
      [-p.k / p.m, -p.b / p.m],
    ],
    B: [[0], [1 / p.m]],
    E: [[0], [1 / p.m]],
    C: [[1, 0]],
    D: 0,
  };
}

export type DampingRegime = 'undamped' | 'underdamped' | 'critically damped' | 'overdamped';

export interface DampingAnalysis {
  applicable: boolean;
  /** Why the second-order labels do or do not apply. */
  notes: string[];
  effectiveStiffness: number;
  effectiveDamping: number;
  wn: number | null;
  zeta: number | null;
  regime: DampingRegime | null;
}

/**
 * Damping ratio and natural frequency for the ideal closed loop:
 * fixed reference, constant feedforward, ideal derivative, no saturation or contact, no integral action.
 */
export function analyzeDamping(cfg: ExperimentConfig): DampingAnalysis {
  const { plant, controller: c } = cfg;
  const notes: string[] = [];
  let kEff = plant.k;
  let bEff = plant.b;
  let applicable = true;
  if (c.kind === 'p' || c.kind === 'pi' || c.kind === 'pid') kEff += c.Kp;
  if (c.kind === 'pid') bEff += c.Kd;
  if ((c.kind === 'pi' || c.kind === 'pid') && c.Ki !== 0) {
    applicable = false;
    notes.push('Integral action adds a third state, so these second-order labels are not a complete description or stability test.');
  }
  if (c.kind === 'bang-bang') {
    applicable = false;
    notes.push('Bang-bang switching is nonlinear; damping ratio does not apply.');
  }
  if (c.kind === 'manual') {
    notes.push('Assumes the manual input stays constant.');
  }
  if (c.kind === 'pid' && c.Kd !== 0 && c.derivTau > 0) {
    notes.push(`The derivative is filtered (τ = ${c.derivTau} s) and sampled, so the true loop only approximates this ideal value.`);
  }
  if (cfg.reference.steps.length > 0 && c.kind !== 'open-loop' && c.kind !== 'manual') {
    notes.push('Applies to the response after each reference change, with the reference then held fixed.');
  }
  notes.push('Ignores thrust saturation and ground contact.');
  if (kEff <= 0) {
    return {
      applicable: false,
      notes: [
        kEff === 0
          ? 'Effective stiffness k + K_p is zero, so there is no restoring force: ω_n and ζ are undefined.'
          : 'Effective stiffness is negative, so the system is unstable; ζ does not apply.',
        ...notes,
      ],
      effectiveStiffness: kEff,
      effectiveDamping: bEff,
      wn: null,
      zeta: null,
      regime: null,
    };
  }
  const wn = Math.sqrt(kEff / plant.m);
  const zeta = bEff / (2 * Math.sqrt(plant.m * kEff));
  let regime: DampingRegime;
  if (bEff === 0) regime = 'undamped';
  else if (Math.abs(zeta - 1) < 1e-9) regime = 'critically damped';
  else if (zeta < 1) regime = 'underdamped';
  else regime = 'overdamped';
  if (bEff < 0) {
    applicable = false;
    notes.unshift('Effective damping is negative, so oscillations grow; the loop is unstable.');
  }
  return { applicable, notes, effectiveStiffness: kEff, effectiveDamping: bEff, wn, zeta, regime };
}

/** Predicted P-control steady error [k r_q − d] / (k + K_p) (fixed T_0 feedforward, ideal sensing). */
export function predictedPSteadyError(plant: PlantParams, Kp: number, rq: number, d: number): number | null {
  const denom = plant.k + Kp;
  if (denom <= 0) return null;
  return (plant.k * rq - d) / denom;
}
